import { useState, useEffect, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { api, fixImageUrl } from '../api'
import { useAuth } from '../AuthContext'
import RinaContextCard from '../components/RinaContextCard'
import AddProductModal from './producer/AddProductModal'
import EditOfferModal from './producer/EditOfferModal'
import { useTheme } from '../theme'
import PageHub from '../components/PageHub'
import { avecAlpha } from '../lib/couleurs'
import { useCanaux, canauxTarifesDeLOffre, netDuCanal } from './producer/canaux'
import { libelleUnite } from './producer/wizard/wizardConstants'

/**
 * Mes offres — onglet 📦 du hub producteur, fidele au prototype v7.2 (OffresView) :
 * tuiles resume (produits / bientot epuises), liste de cartes avec jauge de stock
 * et rangee « Vendu sur » (chips canaux actifs + « Gerer › » → feuille canaux
 * glassDark avec interrupteurs). Un meme stock, reparti sur les canaux.
 */

// Canaux de vente : catalogue (lieux du portail × activations de l'org) et regle
// de disponibilite dans `producer/canaux.js` (source unique, S69/S70). Un lieu
// masque au BO n'apparait pas ; un canal non ouvert ne s'active plus ici.

// Etat d'un canal pour une offre : principal (module de l'offre) ou additionnel actif
const canalActif = (offer, code) =>
  offer.module?.code === code
  || (offer.channels || []).some(ch => ch.module?.code === code && ch.isActive)

// Canaux a afficher pour une offre : le catalogue de l'organisation, rien
// d'autre (S72, Enzo) — un canal ferme ou masque, meme principal, n'y figure
// pas : un module non active n'est pas un endroit ou le produit se vend.
const canauxDeLOffre = (catalogue) => catalogue

// Niveau de stock (proto dangerOf) : ratio stock / seuil d'alerte. Sans seuil
// renseigne, celui du prototype (20) — la jauge existe des qu'il y a un stock.
const SEUIL_PAR_DEFAUT = 20
const dangerOf = (offer) => {
  const stock = offer.stock != null ? Number(offer.stock) : null
  const seuil = offer.stockAlerte != null && Number(offer.stockAlerte) > 0 ? Number(offer.stockAlerte) : SEUIL_PAR_DEFAUT
  if (stock == null) return null
  const r = stock / seuil
  if (r >= 0.6) return { c: '#1a9e88', l: 'Confortable', r }
  if (r >= 0.25) return { c: 'rgb(var(--c-primary))', l: 'Stock tendu', r }
  return { c: '#d9534f', l: 'Bientôt épuisé', r }
}

/* Feuille canaux (proto : bottom sheet glassDark « Où vendre ce produit ? ») */
function CanauxSheet({ offer, catalogue, onClose, onToggle, busy }) {
  return createPortal(
    <div onClick={onClose}
      className="fixed inset-0 z-[110] flex items-end"
      style={{ background: 'rgba(4,25,24,0.5)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}>
      <div onClick={(e) => e.stopPropagation()}
        className="glass-dark w-full rounded-t-[30px] px-5 pt-5 pb-9 animate-fade-up"
        style={{ color: 'rgb(var(--c-creme))' }}>
        <div className="w-[42px] h-1 rounded-full mx-auto mb-4" style={{ background: 'rgba(247,240,234,0.3)' }} />
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-center" style={{ color: 'rgba(247,240,234,0.6)' }}>
          {offer.product?.category?.emoji || '📦'} {offer.customName || offer.product?.nom}
        </p>
        <p className="font-serif text-[21px] text-center mt-1">Où vendre ce produit ?</p>
        <p className="text-[12px] text-center mt-1" style={{ color: 'rgba(247,240,234,0.6)' }}>
          Un même stock, réparti sur vos canaux.
        </p>
        <div className="flex flex-col gap-2.5 mt-4">
          {canauxDeLOffre(catalogue).map((ch) => {
            const principal = offer.module?.code === ch.code
            // Canal non ouvert (« Bientôt », ou module non active pour l'org) :
            // visible, ETEINT, non activable — meme s'il est le canal principal
            // de l'offre. Un produit ne se vend pas dans un module ferme (Enzo, S72).
            const ferme = !ch.ouvert
            const on = !ferme && canalActif(offer, ch.code)
            return (
              <button key={ch.code}
                onClick={() => !principal && !ferme && !busy && onToggle(offer.id, ch.code, !on)}
                disabled={principal || ferme || busy === ch.code}
                className="flex items-center gap-3.5 px-4 py-3.5 rounded-[20px] text-left"
                style={{
                  background: on ? avecAlpha(ch.c, 0.15) : 'rgba(247,240,234,0.06)',
                  border: on ? `1.5px solid ${ch.c}` : '1px solid rgba(247,240,234,0.15)',
                  color: 'rgb(var(--c-creme))',
                  opacity: ferme ? 0.45 : 1,
                }}>
                <span className="w-11 h-11 rounded-2xl flex items-center justify-center text-[21px] flex-shrink-0"
                  style={{ background: avecAlpha(ch.c, 0.2) }}>{ch.e}</span>
                <span className="flex-1">
                  <span className="block text-[14.5px] font-bold">{ch.l}</span>
                  <span className="block text-[11.5px]" style={{ color: 'rgba(247,240,234,0.55)' }}>
                    {ferme ? ch.raison : principal ? 'Canal principal — toujours actif' : ch.d}
                  </span>
                </span>
                <span className="relative w-[46px] h-[27px] rounded-full flex-shrink-0 transition-colors"
                  style={{ background: on ? ch.c : 'rgba(247,240,234,0.2)', opacity: principal ? 0.6 : 1 }}>
                  <span className="absolute top-[3px] w-[21px] h-[21px] rounded-full bg-white transition-all"
                    style={{ left: on ? 22 : 3 }} />
                </span>
              </button>
            )
          })}
        </div>
        <button onClick={onClose}
          className="mt-4 w-full py-3.5 rounded-full font-extrabold text-[14px]"
          style={{ background: 'rgb(var(--c-creme))', color: 'rgb(var(--c-green))' }}>Terminé</button>
      </div>
    </div>,
    document.body
  )
}

function ContenuOffres() {
  const { isWeb3 } = useTheme()
  const { token } = useAuth()
  const tc = 'text-ink'
  const ts = 'text-ink/60'
  const tm = 'text-ink/60'
  const cardBg = 'panneau-verre'
  const inputBg = isWeb3 ? 'bg-white/5 text-white border-white/10' : 'bg-white text-ink border-ink/10'

  const [org, setOrg] = useState(null)
  const [offers, setOffers] = useState([])
  const [myProposals, setMyProposals] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAddProduct, setShowAddProduct] = useState(false)
  const [editingOffer, setEditingOffer] = useState(null)
  const [canauxForId, setCanauxForId] = useState(null) // offre dont on edite les canaux
  const [channelBusy, setChannelBusy] = useState(null)
  const [toast, setToast] = useState(null)

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 3000)
  }

  const loadData = useCallback(async () => {
    if (!token) return
    try {
      const [orgData, offersData, proposalsData] = await Promise.all([
        api.producer.getMe().catch(() => null),
        api.producer.getOffers().catch(() => []),
        api.producer.getMyProposals().catch(() => []),
      ])
      setOrg(orgData)
      setOffers(Array.isArray(offersData) ? offersData : [])
      setMyProposals(Array.isArray(proposalsData) ? proposalsData : [])
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => { loadData() }, [loadData])

  const modules = org?.moduleActivations || []
  const catalogue = useCanaux(modules)

  const handleWizardSuccess = (msg) => {
    showToast(msg)
    loadData()
  }

  // Toggle d'un canal de vente (le backend renvoie les offres a jour)
  const handleToggleChannel = async (offerId, moduleCode, isActive) => {
    setChannelBusy(moduleCode)
    try {
      const updated = await api.producer.setOfferChannel(offerId, moduleCode, isActive)
      if (Array.isArray(updated)) {
        setOffers(updated)
        const fresh = updated.find(o => o.id === offerId)
        if (fresh && editingOffer?.id === offerId) setEditingOffer(fresh)
      }
    } catch (err) {
      showToast(err.message, 'error')
    } finally {
      setChannelBusy(null)
    }
  }

  // Stock (prototype v7.3.x, retour Enzo S72) : le compteur unique se corrige
  // ICI et seulement ici — maladie, casse, vente hors circuit. Optimiste, puis
  // une seule ecriture par offre une fois les taps termines.
  const stockTimers = useRef({})
  const ajusterStock = (o, delta) => {
    const q = Math.max(0, Number(o.stock || 0) + delta)
    setOffers((os) => os.map((x) => (x.id === o.id ? { ...x, stock: q } : x)))
    clearTimeout(stockTimers.current[o.id])
    stockTimers.current[o.id] = setTimeout(() => {
      api.producer.updateOffer(o.id, { stock: q }).catch((err) => {
        showToast(err.message || 'Le stock n\'a pas pu être enregistré', 'error')
        loadData()
      })
    }, 500)
  }

  // Photo prioritaire : customPhotos de l'offre > photos du produit > emoji categorie
  const getOfferPhoto = (offer) => {
    if (offer.customPhotos?.length > 0) return fixImageUrl(offer.customPhotos[0])
    if (offer.product?.photos?.length > 0) return fixImageUrl(offer.product.photos[0])
    return null
  }

  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map(i => (
          <div key={i} className={`h-16 rounded-xl animate-pulse bg-ink/5`} />
        ))}
      </div>
    )
  }

  const nbEpuises = offers.filter(o => { const d = dangerOf(o); return d && d.r < 0.25 }).length
  const canauxOffer = canauxForId ? offers.find(o => o.id === canauxForId) : null
  // Une proposition qui a deja son offre (prototype : la proposition enchaine
  // sur l'offre, S71) s'affiche comme offre « en validation », pas deux fois.
  const pending = myProposals.filter(p => p.status === 'DRAFT' && !offers.some(o => o.product?.id === p.id))

  return (
    <>
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-[200] px-4 py-2.5 rounded-xl text-sm font-medium shadow-lg animate-fade-up ${
          toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-600 text-white'
        }`}>
          {toast.msg}
        </div>
      )}

      {(
        <RinaContextCard
          tc={tc}
         
          message="Voici tout ton stock, géré une seule fois. Touche « Gérer » pour activer ou couper un canal de vente."
        />
      )}

      {/* ===== Resume (proto : 2 tuiles serif) ===== */}
      <div className="flex gap-2.5 mb-3">
        <div className={`flex-1 rounded-2xl px-3.5 py-3 text-center ${cardBg}`}>
          <p className={`font-serif text-[22px] ${tc}`}>{offers.length}</p>
          <p className={`text-[10.5px] ${ts}`}>produits</p>
        </div>
        <div className={`flex-1 rounded-2xl px-3.5 py-3 text-center ${cardBg}`}>
          <p className="font-serif text-[22px]" style={{ color: nbEpuises ? '#d9534f' : '#1a9e88' }}>{nbEpuises}</p>
          <p className={`text-[10.5px] ${ts}`}>bientôt épuisé{nbEpuises > 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* ===== En-tete « Mes produits » + Ajouter (violet module offres) ===== */}
      <div className="flex items-center justify-between mb-2.5 px-0.5">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: 'rgb(var(--c-teal))' }}>Mes produits</p>
        <button onClick={() => setShowAddProduct(true)}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-white font-extrabold text-[13px]"
          style={{ background: '#6b5b95', boxShadow: '0 4px 14px rgba(107,91,149,0.35)' }}>
          <span className="text-[16px] leading-none">+</span> Ajouter
        </button>
      </div>

      {/* Propositions en attente */}
      {pending.length > 0 && (
        <div className="mb-3">
          {pending.map((p) => (
            <div key={p.id} className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl mb-1.5 ${cardBg}`}>
              <div className="w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 bg-gradient-to-br from-ink/10 to-ink/20 flex items-center justify-center">
                {p.photos?.length > 0 ? (
                  <img src={fixImageUrl(p.photos[0])} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-sm">{p.category?.emoji || '📦'}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-[13px] font-medium truncate ${tc}`}>{p.nom}</p>
                <p className={`text-[10px] ${ts}`}>{p.category?.nom}</p>
              </div>
              <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700">⏳ En attente</span>
            </div>
          ))}
        </div>
      )}

      {offers.length === 0 ? (
        <div className="text-center pt-6 animate-fade-up">
          <span className="text-4xl block mb-3">🌿</span>
          <p className={`text-sm font-semibold mb-1 ${tc}`}>Faites découvrir vos produits</p>
          <p className={`text-xs mb-4 ${ts}`}>Ajoutez vos produits pour les proposer sur les marchés et évènements Lyannaj</p>
          <button
            onClick={() => setShowAddProduct(true)}
            className="px-5 py-2.5 rounded-xl text-white text-sm font-semibold active:scale-[0.98] transition-transform"
            style={{ background: '#6b5b95' }}
          >
            Commencer
          </button>
        </div>
      ) : (
        /* ===== Liste des offres (proto : carte + jauge + « Vendu sur ») ===== */
        <div className="flex flex-col gap-3">
          {offers.map((o, i) => {
            const d = dangerOf(o)
            const tileC = d?.c || 'rgb(var(--c-teal))'
            // Seuls les canaux OUVERTS comptent : un module non active pour
            // l'organisation n'est pas un endroit ou le produit se vend (S72).
            const actifs = canauxDeLOffre(catalogue).filter(ch => ch.ouvert && canalActif(o, ch.code))
            return (
              <div key={o.id} className={`rounded-[22px] px-4 py-3.5 animate-fade-up ${cardBg}`}
                style={{ animationDelay: `${0.05 + i * 0.03}s`, opacity: o.status === 'ACTIVE' ? 1 : 0.65 }}>
                <div className="flex items-center gap-3">
                  <span className="w-12 h-12 rounded-[15px] flex items-center justify-center text-[25px] flex-shrink-0 overflow-hidden"
                    style={{ background: avecAlpha(tileC, 0.12), border: `1px solid ${avecAlpha(tileC, 0.25)}` }}>
                    {getOfferPhoto(o)
                      ? <img src={getOfferPhoto(o)} alt="" className="w-full h-full object-cover rounded-[14px]" />
                      : (o.product?.category?.emoji || '📦')}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-[15px] font-bold truncate ${tc}`}>{o.customName || o.product?.nom || 'Produit'}</p>
                    {o.product?.status === 'DRAFT' && (
                      <span className="inline-block text-[10px] font-bold px-2 py-0.5 rounded-full mb-0.5"
                        style={{ background: 'rgb(var(--c-primary) / 0.12)', color: 'rgb(var(--c-primary))' }}>
                        🕓 Référence en validation
                      </span>
                    )}
                    <p className={`text-[12px] ${ts}`}>
                      {/* Net producteur = « Je veux toucher » de la feuille ✏️ : MEME calcul
                          (canauxTarifesDeLOffre, S75) — prix du 1er canal ouvert (souvent
                          Evenements, prixCanal) x (1 − taux du canal). */}
                      Vous recevez <span className={`font-bold ${tc}`} style={{ fontFamily: 'Georgia,serif' }}>
                        {(() => {
                          const c = canauxTarifesDeLOffre(catalogue, o)[0]
                          const net = c ? netDuCanal(c) : Number(o.prix || 0) * (1 - Number(o.commissionRate ?? 0) / 100)
                          return net.toFixed(2).replace('.', ',')
                        })()}€</span> / {libelleUnite(o.prixUnit)}
                      {o.status === 'RUPTURE' ? ' · rupture' : o.status === 'INACTIVE' ? ' · désactivée' : ''}
                    </p>
                  </div>
                  <button onClick={() => setEditingOffer(o)} aria-label="Modifier l'offre"
                    className={`rounded-xl px-2.5 py-2 text-[13px] ${cardBg}`}>✏️</button>
                </div>

                {/* Jauge + AJUSTEMENT DU STOCK (prototype v7.3.x) : − quantite + */}
                {d && (
                  <div className="mt-3">
                    <div className="flex justify-between items-center mb-[5px]">
                      <span className="text-[11.5px] font-bold" style={{ color: d.c }}>● {d.l}</span>
                      <div className="flex items-center gap-[7px]">
                        <button onClick={() => ajusterStock(o, -1)} disabled={Number(o.stock) <= 0} aria-label="Réduire le stock"
                          className="w-[26px] h-[26px] rounded-[9px] text-sm font-extrabold text-ink disabled:opacity-35"
                          style={{ background: 'rgba(7,62,61,0.08)' }}>−</button>
                        <span className="text-[12px] font-extrabold text-ink text-center" style={{ minWidth: 62 }}>
                          {Number(o.stock)} {libelleUnite(o.stockUnit || o.prixUnit)}
                        </span>
                        <button onClick={() => ajusterStock(o, 1)} aria-label="Augmenter le stock"
                          className="w-[26px] h-[26px] rounded-[9px] text-sm font-extrabold text-ink"
                          style={{ background: 'rgba(20,184,166,0.18)' }}>+</button>
                      </div>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(7,62,61,0.08)' }}>
                      <div className="h-full rounded-full transition-all"
                        style={{ width: `${Math.max(6, Math.min(100, d.r * 100))}%`, background: d.c }} />
                    </div>
                  </div>
                )}

                {/* canaux : lecture compacte + ouverture de la feuille */}
                <button onClick={() => setCanauxForId(o.id)}
                  className={`mt-3 w-full flex items-center gap-2 rounded-[14px] px-3 py-2.5 text-left ${cardBg}`}>
                  <span className="text-[9.5px] font-bold uppercase tracking-wider text-ink/45 flex-shrink-0">Vendu sur</span>
                  <span className="flex-1 flex gap-1.5 flex-wrap">
                    {actifs.length ? actifs.map(ch => (
                      <span key={ch.code} className="inline-flex items-center gap-1 text-[11.5px] font-bold px-2 py-0.5 rounded-full"
                        style={{ color: ch.c, background: avecAlpha(ch.c, 0.12) }}>{ch.e} {ch.l}</span>
                    )) : <span className="text-[11.5px] font-semibold text-[#d9534f]">Aucun canal actif</span>}
                  </span>
                  <span className={`text-[12px] font-bold flex-shrink-0 ${tc}`}>Gérer ›</span>
                </button>
              </div>
            )
          })}
        </div>
      )}

      {/* ========== FEUILLE : canaux de vente ========== */}
      {canauxOffer && (
        <CanauxSheet
          offer={canauxOffer}
          catalogue={catalogue}
          busy={channelBusy}
          onToggle={handleToggleChannel}
          onClose={() => setCanauxForId(null)}
        />
      )}

      {/* ========== MODAL: Wizard ajout offre ==========
          Monte a l'ouverture, demonte a la fermeture (S71, retour Enzo : ferme
          en cours de route puis rouvert, il montrait l'ancienne etape le temps
          que son effet de remise a zero passe). Un composant neuf n'a rien a
          remettre a zero, et apparait a son premier rendu. */}
      {showAddProduct && (
        <AddProductModal
 tc={tc} ts={ts} tm={tm} cardBg={cardBg} inputBg={inputBg}
          isOpen
          onClose={() => setShowAddProduct(false)}
          onSuccess={handleWizardSuccess}
          modules={modules}
        />
      )}

      {/* ========== MODAL: Modifier une offre (feuille du prototype, autonome) ========== */}
      {editingOffer && (
        <EditOfferModal
          offer={editingOffer}
          catalogue={catalogue}
          onClose={() => setEditingOffer(null)}
          onSaved={() => { setEditingOffer(null); showToast('Offre mise à jour !'); loadData() }}
          onDeleted={() => { setEditingOffer(null); showToast('Offre supprimée'); loadData() }}
        />
      )}
    </>
  )
}

// La page porte SON layout (S51) : retour et barre declares ici, la route
// d'App.jsx ne fait que router.
export default function PageOffres() {
  return <PageHub><ContenuOffres /></PageHub>
}

